import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import ProductVisual from './ProductVisual';
import { products } from '../data/content';

export default function Products() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  const product = products[active];

  // Links like #product-stp (footer, hero) open the matching tab.
  useEffect(() => {
    const syncFromHash = () => {
      const match = window.location.hash.match(/^#product-(\w+)$/);
      if (!match) return;
      const index = products.findIndex((p) => p.id === match[1]);
      if (index >= 0) {
        setActive(index);
        document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
      }
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  const go = (index) => {
    const next = (index + products.length) % products.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const onTabKey = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      go(active + 1);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      go(active - 1);
    }
  };

  return (
    <section id="products" className="relative overflow-hidden bg-ink py-24 text-white lg:py-32">
      <div className="grid-lines pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -right-40 top-20 h-[30rem] w-[30rem] rounded-full bg-brand-700/20 blur-3xl" />

      <div className="container-x relative">
        <div className="reveal grid gap-6 lg:grid-cols-2 lg:items-end">
          <div>
            <span className="eyebrow !text-brand-400 before:!bg-brand-400">Our Product Range</span>
            <h2 className="mt-4 text-4xl font-bold leading-[1.05] sm:text-5xl">
              Five plants.
              <br />
              <span className="text-outline text-white/70">One standard.</span>
            </h2>
          </div>
          <p className="max-w-md text-gray-400 lg:justify-self-end">
            From drinking water to zero liquid discharge — every plant is individually engineered
            for its application, capacity and discharge norms.
          </p>
        </div>

        <div className="reveal mt-14 grid overflow-hidden rounded-[2rem] bg-ink-soft ring-1 ring-white/10 lg:grid-cols-[300px_1fr]">
          {/* selector */}
          <div
            role="tablist"
            aria-label="Products"
            aria-orientation="vertical"
            className="no-scrollbar flex gap-2 overflow-x-auto border-b border-white/10 p-3 lg:flex-col lg:overflow-visible lg:border-b-0 lg:border-r lg:p-4"
          >
            {products.map((p, i) => {
              const selected = i === active;
              return (
                <button
                  key={p.id}
                  ref={(el) => (tabRefs.current[i] = el)}
                  role="tab"
                  id={`tab-${p.id}`}
                  aria-selected={selected}
                  aria-controls="product-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(i)}
                  onKeyDown={onTabKey}
                  className={`group relative flex min-w-[9.5rem] flex-none items-center gap-4 rounded-2xl px-4 py-4 text-left transition lg:min-w-0 ${
                    selected ? 'bg-brand-600 shadow-glow' : 'hover:bg-white/5'
                  }`}
                >
                  <span className={`font-display text-xs ${selected ? 'text-white/70' : 'text-gray-500'}`}>0{i + 1}</span>
                  <span>
                    <span className="block font-display text-2xl font-bold leading-none">{p.code}</span>
                    <span className={`mt-1 hidden text-xs lg:block ${selected ? 'text-white/80' : 'text-gray-400'}`}>
                      {p.name}
                    </span>
                  </span>
                  {selected && <ArrowRight className="ml-auto hidden h-4 w-4 lg:block" />}
                </button>
              );
            })}
          </div>

          {/* panel */}
          <div
            id="product-panel"
            role="tabpanel"
            aria-labelledby={`tab-${product.id}`}
            key={product.id}
            className="grid gap-8 p-5 sm:p-8 xl:grid-cols-[1fr_1fr] xl:p-10"
          >
            <div className="relative xl:min-h-[30rem]">
              <ProductVisual
                product={product}
                className="aspect-[4/3] h-full w-full rounded-3xl bg-[#1d2126] ring-1 ring-white/10 [animation:fadeIn_.5s_ease] xl:absolute xl:inset-0 xl:aspect-auto"
              />
              <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 rounded-b-3xl bg-gradient-to-t from-ink/85 to-transparent" />
              <span className="pointer-events-none absolute bottom-3 right-5 font-display text-7xl font-bold leading-none text-white/90 drop-shadow-lg sm:text-8xl">
                {product.code}
              </span>
              <span className="absolute left-4 top-4 rounded-full bg-ink/80 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/15 backdrop-blur">
                {product.subtitle}
              </span>
            </div>

            <div className="flex flex-col [animation:fadeIn_.5s_ease]">
              <h3 className="text-2xl font-bold sm:text-3xl">{product.name}</h3>
              <ul className="mt-6 space-y-3">
                {product.points.map((pt) => (
                  <li key={pt.slice(0, 40)} className="flex gap-3 text-[0.95rem] leading-relaxed text-gray-300">
                    <Check className="mt-1 h-4 w-4 flex-none text-brand-500" />
                    {pt}
                  </li>
                ))}
              </ul>
              {product.tags && (
                <div className="mt-6">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{product.tagsLabel}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {product.tags.map((t) => (
                      <span key={t} className="rounded-full bg-white/5 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/15">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-auto flex items-center justify-between gap-4 pt-8">
                <span className="font-display text-sm text-gray-500">
                  <span className="text-white">0{active + 1}</span> / 0{products.length}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => go(active - 1)}
                    className="rounded-full p-3 ring-1 ring-white/20 transition hover:bg-white hover:text-ink"
                    aria-label="Previous product"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => go(active + 1)}
                    className="rounded-full bg-brand-600 p-3 transition hover:bg-brand-700"
                    aria-label="Next product"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
